import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Layout/Header';
import Modal from '../components/UI/Modal';
import { money } from '../utils/format';
import { hasPermission, canViewCosts } from '../utils/permissions';
import ProductDetail from '../components/UI/ProductDetail';
import { Plus, Search, ChevronUp, ChevronDown, ChevronsUpDown, Archive, RotateCcw, MapPin, Camera, X } from 'lucide-react';

export default function Products() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [supFilter, setSupFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name:'', sku:'', category_id:'', unit_type:'', unit_size:'', default_supplier_id:'', unit_cost:'', description:'', location_ids:[] });
  const [sortCol, setSortCol] = useState(null);
  const [viewProductId, setViewProductId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sortDir, setSortDir] = useState('asc');

  const load = () => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (catFilter) p.set('category_id', catFilter);
    if (supFilter) p.set('supplier_id', supFilter);
    if (showArchived) p.set('include_archived', '1');
    api('/api/products?' + p).then(r => setProducts(r.data)).catch(console.error);
  };

  useEffect(() => {
    api('/api/categories').then(r => setCategories(r.data)).catch(() => {});
    api('/api/suppliers').then(r => setSuppliers(r.data)).catch(() => {});
    api('/api/locations').then(r => setLocations(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [search, catFilter, supFilter, showArchived]);

  const [searchParams, setSearchParams] = useSearchParams();
  const routeLocation = useLocation();
  useEffect(() => {
    const state = routeLocation.state;
    if (searchParams.get('create') === 'true' || state?.openAdd) {
      const timer = setTimeout(() => {
        if (state?.defaultLocation) {
          setForm(prev => ({
            ...prev, name:'', sku:'', category_id:'', unit_type:'', unit_size:'',
            default_supplier_id:'', unit_cost:'', description:'',
            location_ids: [state.defaultLocation]
          }));
          setModal('add');
        } else {
          openAdd();
        }
        if (searchParams.get('create') === 'true') setSearchParams({}, { replace: true });
        if (state?.openAdd) window.history.replaceState({}, document.title);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);

  const toggleSort = (col) => { if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(col); setSortDir('asc'); } };
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown size={12} style={{ opacity:.3, marginLeft:4 }} />;
    return sortDir === 'asc' ? <ChevronUp size={12} style={{ color:'var(--gold)', marginLeft:4 }} /> : <ChevronDown size={12} style={{ color:'var(--gold)', marginLeft:4 }} />;
  };

  const sorted = useMemo(() => {
    if (!sortCol) return products;
    const copy = [...products];
    copy.sort((a, b) => {
      switch (sortCol) {
        case 'name': { const c = (a.name||'').localeCompare(b.name||''); return sortDir === 'asc' ? c : -c; }
        case 'sku': { const c = (a.sku||'').localeCompare(b.sku||''); return sortDir === 'asc' ? c : -c; }
        case 'category': { const c = (a.category_name||'').localeCompare(b.category_name||''); return sortDir === 'asc' ? c : -c; }
        case 'unit': { const c = (a.unit_type||'').localeCompare(b.unit_type||''); return sortDir === 'asc' ? c : -c; }
        case 'cost': return sortDir === 'asc' ? Number(a.unit_cost)-Number(b.unit_cost) : Number(b.unit_cost)-Number(a.unit_cost);
        case 'supplier': { const c = (a.supplier_name||'').localeCompare(b.supplier_name||''); return sortDir === 'asc' ? c : -c; }
        case 'locations': return sortDir === 'asc' ? (a.location_ids||[]).length - (b.location_ids||[]).length : (b.location_ids||[]).length - (a.location_ids||[]).length;
        default: return 0;
      }
    });
    return copy;
  }, [products, sortCol, sortDir]);

  const toggleLoc = (lid) => {
    setForm(prev => ({ ...prev, location_ids: prev.location_ids.includes(lid) ? prev.location_ids.filter(id => id !== lid) : [...prev.location_ids, lid] }));
  };

  const openAdd = () => {
    setForm({ name:'', sku:'', category_id:'', unit_type:'', unit_size:'', default_supplier_id:'', unit_cost:'', description:'', location_ids: locations.map(l => l.id) });
    setModal('add');
  };
  const openEdit = (p) => {
    setForm({ name:p.name, sku:p.sku||'', category_id:p.category_id, unit_type:p.unit_type, unit_size:p.unit_size||'', default_supplier_id:p.default_supplier_id||'', unit_cost:p.unit_cost, description:p.description||'', id:p.id, location_ids: p.location_ids || [] });
    setModal('edit');
  };

  const submit = async () => {
    try {
      const body = { ...form, unit_cost:parseFloat(form.unit_cost)||0, category_id:parseInt(form.category_id), default_supplier_id:form.default_supplier_id ? parseInt(form.default_supplier_id) : null };
      if (modal === 'edit') await api('/api/products/'+form.id, { method:'PUT', body:JSON.stringify(body) });
      else await api('/api/products', { method:'POST', body:JSON.stringify(body) });
      setModal(null); load();
    } catch (err) { alert(err.error || 'Failed'); }
  };

  const archiveProduct = async (p) => {
    if (!confirm('Archive "' + p.name + '"? It will be removed from inventory but kept for reporting.')) return;
    await api('/api/products/' + p.id, { method: 'DELETE' });
    load();
  };
  const restoreProduct = async (p) => {
    await api('/api/products/' + p.id + '/restore', { method: 'PUT' });
    load();
  };

  const uploadPhoto = async (productId, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const token = localStorage.getItem('token');
      const res = await fetch('/api/products/' + productId + '/photo', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: formData,
      });
      const data = await res.json();
      if (data.success) load();
      else alert(data.error || 'Upload failed');
    } catch (err) { alert('Upload failed'); }
    setUploading(false);
  };

  const removePhoto = async (productId) => {
    await api('/api/products/' + productId + '/photo', { method: 'DELETE' });
    load();
  };

  const showCosts = canViewCosts(user);
  const canEditProducts = hasPermission(user, 'edit_products');
  const canEdit = canEditProducts;
  const locName = (id) => locations.find(l => l.id === id)?.name || '?';
  const thSort = (label, col) => (
    <th onClick={() => toggleSort(col)} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
      <span style={{ display:'inline-flex', alignItems:'center' }}>{label}<SortIcon col={col} /></span>
    </th>
  );

  return (
    <div>
      <Header title="Products">
        {canEdit && <button className="btn btn-primary" onClick={openAdd}><Plus size={16} /> Add Product</button>}
      </Header>

      <div className="tabs">
        <button className={'tab'+(!catFilter?' active':'')} onClick={() => setCatFilter('')}>All</button>
        {categories.map(c => (
          <button key={c.id} className={'tab'+(catFilter==c.id?' active':'')} onClick={() => setCatFilter(c.id)} style={catFilter==c.id?{}:{borderBottom:'2px solid '+c.color}}>
            {c.name.split(' ')[0]}
          </button>
        ))}
      </div>

      <div className="filters">
        <select value={supFilter} onChange={e => setSupFilter(e.target.value)}>
          <option value="">All Suppliers</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="search-input"><Search size={16} /><input placeholder="Search by name or SKU..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--muted)', cursor:'pointer', whiteSpace:'nowrap', userSelect:'none' }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} style={{ width:'auto', accentColor:'var(--faint)' }} />
          Show archived
        </label>
        <div style={{ fontSize:13, color:'var(--faint)' }}>{sorted.length} product{sorted.length !== 1 ? 's' : ''}</div>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr>
            {thSort('Product','name')}
            {thSort('SKU','sku')}
            {thSort('Category','category')}
            {thSort('Unit','unit')}
            {showCosts && thSort('Cost','cost')}
            {thSort('Supplier','supplier')}
            {thSort('Locations','locations')}
            {canEdit && <th>Actions</th>}
          </tr></thead>
          <tbody>
            {sorted.map(p => (
              <tr key={p.id} style={{ opacity: p.is_active ? 1 : .5 }}>
                <td>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  {p.photo_url && <img src={p.photo_url} alt="" style={{ width:28, height:28, borderRadius:6, objectFit:'cover', flexShrink:0 }} />}
                  <span onClick={(e) => { e.stopPropagation(); setViewProductId(p.id); }} style={{ fontWeight:600, cursor:'pointer', borderBottom:'1px dashed var(--faint)' }} onMouseEnter={e => e.currentTarget.style.color='var(--gold)'} onMouseLeave={e => e.currentTarget.style.color='var(--text)'}>{p.name}</span>
                </div>
                  {!p.is_active && <span style={{ fontSize:10, color:'var(--faint)', background:'var(--faint)18', padding:'1px 6px', borderRadius:6, fontWeight:600 }}>ARCHIVED</span>}
                </td>
                <td style={{ color:'var(--faint)' }}>{p.sku}</td>
                <td><span className="cat-badge" style={{ background:(p.category_color||'#888')+'20', color:p.category_color }}>{p.category_name}</span></td>
                <td>{p.unit_type}{p.unit_size ? ` (${p.unit_size})` : ''}</td>
                {showCosts && <td>{money(p.unit_cost)}</td>}
                <td>{p.supplier_name || '-'}</td>
                <td>
                  <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                    {(p.location_ids || []).length === 0 && <span style={{ fontSize:11, color:'var(--danger)', fontStyle:'italic' }}>None</span>}
                    {(p.location_ids || []).length === locations.length && locations.length > 0 && <span style={{ fontSize:11, color:'var(--gold)' }}>All ({locations.length})</span>}
                    {(p.location_ids || []).length > 0 && (p.location_ids || []).length < locations.length && (
                      <span style={{ fontSize:11, color:'var(--muted)' }}>{(p.location_ids||[]).length} loc{(p.location_ids||[]).length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </td>
                {canEdit && (
                  <td>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>Edit</button>
                      {p.is_active ? (
                        <button className="btn btn-secondary btn-sm" onClick={() => archiveProduct(p)} title="Archive" style={{ color:'var(--faint)' }}><Archive size={13} /></button>
                      ) : (
                        <button className="btn btn-secondary btn-sm" onClick={() => restoreProduct(p)} title="Restore" style={{ color:'var(--success)' }}><RotateCcw size={13} /></button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length === 0 && <p style={{ padding:20, textAlign:'center', color:'var(--muted)' }}>No products found</p>}

      {modal && (
        <Modal title={modal === 'edit' ? 'Edit Product' : 'Add Product'} onClose={() => setModal(null)}>
          <div className="form-group"><label>Name</label><input value={form.name} onChange={e => setForm({...form, name:e.target.value})} required /></div>
          <div className="grid-2">
            <div className="form-group"><label>SKU</label><input value={form.sku} onChange={e => setForm({...form, sku:e.target.value})} /></div>
            {showCosts && <div className="form-group"><label>Unit Cost ($)</label><input type="number" value={form.unit_cost} onChange={e => setForm({...form, unit_cost:e.target.value})} min="0" step="0.01" /></div>}
          </div>
          <div className="grid-2">
            <div className="form-group"><label>Category</label>
              <select value={form.category_id} onChange={e => setForm({...form, category_id:e.target.value})} required>
                <option value="">Select...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Supplier</label>
              <select value={form.default_supplier_id} onChange={e => setForm({...form, default_supplier_id:e.target.value})}>
                <option value="">None</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label>Unit Type</label><input value={form.unit_type} onChange={e => setForm({...form, unit_type:e.target.value})} placeholder="e.g. case, keg, bottle" required /></div>
            <div className="form-group"><label>Unit Size</label><input value={form.unit_size} onChange={e => setForm({...form, unit_size:e.target.value})} placeholder="e.g. 24 ct" /></div>
          </div>

          {/* Location Assignment */}
          <div className="form-group">
            <label><MapPin size={13} style={{ verticalAlign:'-2px', marginRight:4 }} />Locations</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:6 }}>
              <label style={{
                display:'flex', alignItems:'center', gap:6, padding:'7px 14px',
                background: form.location_ids.length === locations.length && locations.length > 0 ? 'var(--gold-pale)' : 'var(--navy)',
                border:'1px solid '+(form.location_ids.length === locations.length && locations.length > 0 ? 'var(--gold)' : 'var(--border)'),
                borderRadius:10, cursor:'pointer', fontSize:13, fontWeight:600,
              }}>
                <input type="checkbox"
                  checked={form.location_ids.length === locations.length && locations.length > 0}
                  onChange={() => setForm(prev => ({...prev, location_ids: prev.location_ids.length === locations.length ? [] : locations.map(l=>l.id)}))}
                  style={{ width:'auto', accentColor:'var(--gold)' }} />
                All Locations
              </label>
              {locations.map(l => (
                <label key={l.id} style={{
                  display:'flex', alignItems:'center', gap:6, padding:'7px 14px',
                  background: form.location_ids.includes(l.id) ? 'var(--gold-pale)' : 'var(--card)',
                  border:'1px solid '+(form.location_ids.includes(l.id) ? 'var(--gold)' : 'var(--border)'),
                  borderRadius:10, cursor:'pointer', fontSize:13,
                }}>
                  <input type="checkbox" checked={form.location_ids.includes(l.id)} onChange={() => toggleLoc(l.id)} style={{ width:'auto', accentColor:'var(--gold)' }} />
                  {l.name}
                </label>
              ))}
            </div>
            {form.location_ids.length === 0 && <div style={{ fontSize:12, color:'var(--danger)', marginTop:4 }}>Select at least one location for the product to appear in inventory</div>}
          </div>

          {/* Photo upload */}
          {modal === 'edit' && form.id && (
            <div className="form-group">
              <label><Camera size={13} style={{ verticalAlign:'-2px', marginRight:4 }} />Product Photo</label>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginTop:6 }}>
                {(() => { const p = products.find(x => x.id === form.id); return p && p.photo_url ? (
                  <div style={{ position:'relative' }}>
                    <img src={p.photo_url} alt="" style={{ width:64, height:64, borderRadius:10, objectFit:'cover', border:'1px solid var(--border)' }} />
                    <button onClick={() => removePhoto(form.id)} style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', background:'var(--danger)', border:'none', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12 }}><X size={12} /></button>
                  </div>
                ) : (
                  <div style={{ width:64, height:64, borderRadius:10, background:'var(--navy)', border:'1px dashed var(--border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Camera size={20} style={{ color:'var(--faint)', opacity:.4 }} />
                  </div>
                ); })()}
                <div>
                  <label style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', background:'var(--card)', border:'1px solid var(--border)', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:500 }}>
                    <Camera size={14} /> {uploading ? 'Uploading...' : 'Upload Photo'}
                    <input type="file" accept="image/*" onChange={e => { if (e.target.files[0]) uploadPhoto(form.id, e.target.files[0]); }} style={{ display:'none' }} />
                  </label>
                  <div style={{ fontSize:11, color:'var(--faint)', marginTop:4 }}>JPG, PNG, WebP. Max 5MB.</div>
                </div>
              </div>
            </div>
          )}

          <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => setForm({...form, description:e.target.value})} rows={2} /></div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={submit}>{modal === 'edit' ? 'Save Changes' : 'Add Product'}</button>
          </div>
        </Modal>
      )}
      {viewProductId && <ProductDetail productId={viewProductId} onClose={() => setViewProductId(null)} />}
    </div>
  );
}
